import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import { EmptyState } from '@/components/EmptyState/EmptyState'
import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Input } from '@/components/ui/input'
import { useAutoFillPages } from '@/hooks/useAutoFillPages'
import type { AllWorkspaceReportsCache } from '@/store/api/Workspace/Workspace.types'

import type { ExtendedReportFilter } from '../Reports'

import { FaceMatchItem } from './FaceMatchItem'
import { ReportsFilterSheet } from './ReportsFilterSheet'
import type { ReportStatusType } from './ReportStatusSelector'
import { SocialAnalysisItem } from './SocialAnalysisItem'

interface AvailableReportsTabProps {
  data: AllWorkspaceReportsCache | undefined
  isFetching: boolean
  loadNextPage: () => void

  selectedOfficeAndTeam: { workspace: string | null; team: string | null }
  onApplyFilters: (filters: {
    workspace: string | null
    team: string | null
    reportType?: ExtendedReportFilter | 'all'
    status?: ReportStatusType
  }) => void

  search: string
  onSearchChange: (value: string) => void
  filter: ExtendedReportFilter | 'all'
  status: ReportStatusType

  showAllOfficesOption: boolean

  autoFetchThreshold: number
}

export const AvailableReportsTab = ({
  data,
  isFetching,
  loadNextPage,
  selectedOfficeAndTeam,
  onApplyFilters,
  search,
  onSearchChange,
  filter,
  status,
  showAllOfficesOption,
  autoFetchThreshold,
}: AvailableReportsTabProps) => {
  const { t } = useTranslation()

  const reports = data?.items ?? []
  const hasMoreData = data?.hasMore ?? true

  useAutoFillPages({
    itemCount: reports.length,
    threshold: autoFetchThreshold,
    hasMore: hasMoreData,
    isFetching,
    loadNextPage,
    resetKey: `${search}|${filter}|${status}|${selectedOfficeAndTeam.workspace}|${selectedOfficeAndTeam.team}`,
  })

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-5">
      <div className="w-full flex gap-6 sm:items-center max-sm:flex-col max-sm:gap-5 mx-3">
        <Input
          placeholder={t('dashboard.reportsScreen.search')}
          className="flex h-9 w-[320px] rounded-lg max-sm:w-full"
          value={search}
          useDebounce={true}
          debounceTime={600}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <ReportsFilterSheet
          selectedOfficeAndTeam={selectedOfficeAndTeam}
          selectedFilter={filter}
          selectedStatus={status}
          onApplyFilters={onApplyFilters}
          showAllOfficesOption={showAllOfficesOption}
        />
      </div>

      <div
        id="availableReportsScrollable"
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
      >
        {isFetching && !data ? (
          <div className="flex items-center justify-center h-[300px]">
            <LottieSpinner />
          </div>
        ) : reports.length === 0 ? (
          <EmptyState
            text={
              search === ''
                ? t('dashboard.reportsScreen.emptyState')
                : `${t('dashboard.reportsScreen.noResultsFor')} "${search}"`
            }
          />
        ) : (
          <InfiniteScroll
            className="flex flex-col"
            dataLength={reports.length}
            next={loadNextPage}
            hasMore={hasMoreData}
            scrollableTarget="availableReportsScrollable"
            loader={
              <p className="w-full text-center text-neutralColor60 font-semibold py-3">
                {t('dashboard.reportsScreen.loading')}
              </p>
            }
            endMessage={
              <p className="w-full text-center text-neutralColor60 font-semibold py-3">
                {reports.length === 0 &&
                  t('dashboard.reportsScreen.endMessage')}
              </p>
            }
          >
            {reports.map((report) =>
              report.type === 'socialcheck' ? (
                <SocialAnalysisItem data={report.data} key={report.data.id} />
              ) : (
                <FaceMatchItem data={report.data} key={report.data._id} />
              ),
            )}
          </InfiniteScroll>
        )}
      </div>
    </div>
  )
}
